import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { FastRegistrationError } from '@patient-ui/shared/models';

import * as FastRegistrationActions from './fast-registration.actions';
import { FastRegistrationService } from './fast-registration.service';

@Injectable()
export class FastRegistrationEffects {
  constructor(
    private actions$: Actions,
    private fastRegistrationService: FastRegistrationService
  ) {}

  VerifyFastRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FastRegistrationActions.VerifyFastRegistration),
      exhaustMap((action) =>
        this.fastRegistrationService
          .verifyFastRegistrationInputs(action.payload)
          .pipe(
            map((response) =>
              FastRegistrationActions.VerifyFastRegistrationSuccess({
                response,
              })
            ),
            catchError((error) => {
              const payload = this.extractErrorPayload(error, 'PP-FR-004');
              return of(
                FastRegistrationActions.VerifyFastRegistrationFailure({
                  payload,
                })
              );
            })
          )
      )
    )
  );

  SubmitFastRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FastRegistrationActions.SubmitFastRegistration),
      exhaustMap((action) =>
        this.fastRegistrationService
          .submitFastRegistrationInputs(action.payload)
          .pipe(
            map((response) =>
              FastRegistrationActions.SubmitFastRegistrationSuccess({
                response,
              })
            ),
            catchError((error) => {
              const payload = this.extractErrorPayload(error, '400');
              return of(
                FastRegistrationActions.SubmitFastRegistrationFailure({
                  payload,
                })
              );
            })
          )
      )
    )
  );

  /**
   * Extracts error payload including patient information from API error response
   * @param error - The HTTP error response
   * @param defaultErrorCode - Default error code to use when error code is not found
   * @returns FastRegistrationError object with error code and patient info
   */
  private extractErrorPayload(
    error: HttpErrorResponse,
    defaultErrorCode: string
  ): FastRegistrationError {
    let errorCode = '';
    let firstName = '';
    let lastName = '';
    let userid = '';

    if (error.status === 400 || error.status === 409) {
      if (error.error && error.error.code) {
        errorCode = error.error.code;

        // Extract patient info from additionalInfo for PP-FR-409 error
        if (errorCode === 'PP-FR-409' && error.error.additionalInfo?.Patient) {
          firstName = error.error.additionalInfo.Patient.firstName || '';
          lastName = error.error.additionalInfo.Patient.lastName || '';
          userid = error.error.additionalInfo.Patient.userid || '';
        }
      } else {
        // Generic data mismatch or Bad request error
        errorCode = defaultErrorCode;
      }
    } else {
      errorCode = '500';
    }

    return {
      error: errorCode,
      firstName,
      lastName,
      userid,
    };
  }
}
